
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

interface ReferralData {
  id: string;
  createdAt: string;
  affiliateName: string;
  userName: string;
  referralCode: string;
  status: string;
  ipAddress: string;
}

interface MobileReferralCardProps {
  referral: ReferralData;
}

export const MobileReferralCard = ({ referral }: MobileReferralCardProps) => {
  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'registered':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("User ID copied to clipboard");
  };

  const formatUserId = (userId: string) => {
    if (!userId || userId.includes("@")) return userId;
    
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm truncate">{userId.substring(0, 8)}...</span>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 flex-shrink-0"
          onClick={() => copyToClipboard(userId)}
        >
          <Copy className="h-3 w-3" />
          <span className="sr-only">Copy</span>
        </Button>
      </div>
    );
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base truncate">{referral.affiliateName}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={getBadgeColor(referral.status)}>
                {referral.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {new Date(referral.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-1 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">User:</span>
            <div className="font-medium">
              {typeof referral.userName === 'string' && !referral.userName.includes("@") ? 
                formatUserId(referral.userName) : 
                <span className="truncate">{referral.userName}</span>
              }
            </div>
          </div>
          
          <div>
            <span className="text-muted-foreground">Referral Code:</span>
            <code className="text-xs bg-muted px-1 py-0.5 rounded block mt-1 break-all">
              {referral.referralCode}
            </code>
          </div>
          
          <div>
            <span className="text-muted-foreground">IP Address:</span>
            <p className="font-medium text-xs">{referral.ipAddress}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
